import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export const useEmailSubscription = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch current subscription status
  const fetchSubscriptionStatus = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email_subscribed')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching subscription status:', error);
        return;
      }

      setIsSubscribed(data?.email_subscribed ?? true);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // Update subscription status
  const updateSubscription = async (subscribed: boolean) => {
    if (!user) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('profiles')
        .update({ email_subscribed: subscribed })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating subscription:', error);
        toast({
          variant: "destructive",
          title: "Chyba",
          description: "Nepodařilo se změnit nastavení emailů"
        });
        return;
      }

      setIsSubscribed(subscribed);
      toast({
        title: "Úspěch",
        description: subscribed 
          ? "Úspěšně jste se přihlásili k odběru emailů"
          : "Úspěšně jste se odhlásili z odběru emailů"
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        variant: "destructive",
        title: "Chyba",
        description: "Nepodařilo se změnit nastavení emailů"
      });
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to emails
  const subscribe = () => updateSubscription(true);

  // Unsubscribe from emails
  const unsubscribe = () => updateSubscription(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [user]);

  return {
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
    refreshStatus: fetchSubscriptionStatus
  };
};